import React, { useRef, useState } from "react";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonGroup, Divider, MenuItem, Popover } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { setSetting } from "actions";

import Button from "components/Button/Button";
import { SecondaryStyles } from "components/Button/Styles";
import { SubMenuPopover } from "components/Button/SubMenuPopover";

import { getSettings } from "selectors/root";

import { useBoolean } from "hooks/useBoolean";

export const DefaultMultiButton = styled(Button)(
  ({ theme }) => `
  padding: ${theme.space[1]}px ${theme.space[2]}px;
`,
);

export const SecondaryMultiButton = styled(DefaultMultiButton)(SecondaryStyles);

const ButtonIcon = styled(FontAwesomeIcon)(
  ({ theme }) => `
  margin: ${theme.space[0]}px;
`,
);
const StartButtonIcon = styled(ButtonIcon)(
  ({ theme }) => `
  margin-right: ${theme.space[1]}px;
`,
);
const EndButtonIcon = styled(ButtonIcon)(
  ({ theme }) => `
  margin-left: ${theme.space[1]}px;
`,
);

export function ContextMenuPopoverButton(props) {
  const {
    ButtonComponent = Button,
    anchorRef = null,
    buttons,
    stickySetting,
    includeSearchSubNavThreshold = null,
    restrictHeightSubNav = false,
    label = "Actions",
    optionsButtonDataTour = "optionsButton",
    popoverHorizontal = "right",
  } = props;

  const dispatch = useDispatch();

  const [activeButton, setActiveButton] = useState(null);

  const [isMenuOpen, _ignored, closeMenu, _ignored2, toggleMenu] =
    useBoolean(false);

  const onClose = () => {
    closeMenu();
    setActiveButton(null);
  };

  // Used to anchor the popover on (something) - if we've been supplied one (eg from the multibutton) use
  // that.  If we haven't, use the ... buttton.
  const localAnchorRef = useRef(null);
  const popoverAnchorRef = anchorRef || localAnchorRef;

  const subNavAnchorRef = useRef(null);

  const activeSubNavItems = React.useMemo(() => {
    return buttons[activeButton]?.subNavItems;
  }, [buttons, activeButton]);

  return (
    <>
      <ButtonComponent
        data-tour={optionsButtonDataTour}
        inline
        onClickCapture={e => {
          e.stopPropagation();
          toggleMenu();
        }}
        type="button"
        ref={localAnchorRef}
        padding={2}
        marginThreshold={1}
      >
        <IconButtonContents
          title={label || <ButtonIcon icon={faEllipsis} />}
          endIcon={label ? faEllipsis : null}
        />
      </ButtonComponent>
      <Popover
        open={isMenuOpen}
        anchorEl={popoverAnchorRef.current}
        data-tour="popover"
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: popoverHorizontal,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: popoverHorizontal,
        }}
        marginThreshold={1}
      >
        {buttons
          .filter(button => !button.hide)
          .map((button, index) => {
            const {
              onClick,
              key,
              title,
              titleBold,
              icon,
              isDisabled,
              dataTour = title,
              dividerAfter = false,
              subNavItems = [],
              tooltip,
            } = button;

            const open = activeButton === index;
            return (
              <React.Fragment key={key || title}>
                <MenuItem
                  onClick={e => {
                    if (isDisabled) {
                      e.stopPropagation();
                    } else if (
                      subNavItems?.length > 0 &&
                      activeButton === null
                    ) {
                      subNavAnchorRef.current = e.currentTarget;
                      setActiveButton(index);
                    } else if (open && activeButton !== null) {
                      setActiveButton(null);
                    } else if (isMenuOpen) {
                      // Only handle the click if the isMenuOpen.  Without this, users can rapidly click the menu
                      // and the onClick can run many times before the menu is closed.

                      stickySetting &&
                        dispatch(setSetting(stickySetting, title));
                      onClose();
                      onClick && onClick(e);
                    }
                  }}
                  disabled={isDisabled}
                  data-tour={dataTour}
                  title={tooltip || (typeof title === "string" ? title : null)}
                >
                  <IconButtonContents
                    startIcon={icon}
                    title={
                      <span className={titleBold ? "font-bold" : ""}>
                        {title}
                      </span>
                    }
                    endIcon={subNavItems?.length > 0 ? faCaretRight : null}
                  />
                </MenuItem>
                {dividerAfter ? <Divider /> : null}
              </React.Fragment>
            );
          })
          .filter(Boolean)}
      </Popover>
      <SubMenuPopover
        subNavItems={activeSubNavItems}
        anchorEl={subNavAnchorRef.current}
        onClose={onClose}
        open={Boolean(activeButton !== null && activeSubNavItems)}
        includeSearchSubNavThreshold={includeSearchSubNavThreshold}
        restrictHeightSubNav={restrictHeightSubNav}
      />
    </>
  );
}

export const IconButtonContents = props => {
  const { startIcon, endIcon, title, spin } = props;

  return (
    <div className="inline-flex items-center">
      {startIcon && <StartButtonIcon icon={startIcon} spin={spin} />}
      {title}
      {endIcon && <EndButtonIcon icon={endIcon} spin={spin} />}
    </div>
  );
};

const FlexButtonGroup = styled(ButtonGroup)`
  flex: 1;
  height: 100%;
`;

export const MultiButtonComponent = ({
  buttons,
  ButtonComponent = DefaultMultiButton,
  fullWidth = false,
  orientation = "horizontal",
  preferEnabledAsDefault = false,
  includeSearchSubNavThreshold = null,
  restrictHeightSubNav = false,
  stickySetting = null,
  flex = false,
  optionsButtonDataTour = "optionsButton",
  popoverHorizontal = "right",
  buttonGroupDataTour = undefined,
  buttonInline = true,
}) => {
  const anchorRef = useRef(null);

  const [
    isDefaultButtonMenuOpen,
    openDefaultButtonMenu,
    closeDefaultButtonMenu,
  ] = useBoolean(false);

  const defaultButtonTitleFromSettings = useSelector(
    // Avoiding circular dependency crashing.
    // getSetting(stickySetting)
    state => getSettings(state)[stickySetting],
  );

  const enabledButtons = buttons.filter(b => !b.isDisabled);
  const defaultButton = React.useMemo(() => {
    // If there is a sticky setting, prefer this as the default.
    if (defaultButtonTitleFromSettings) {
      const defaultButtonFromSettings = buttons.find(
        b => b.title === defaultButtonTitleFromSettings,
      );
      if (defaultButtonFromSettings) {
        return defaultButtonFromSettings;
      }
    }
    return preferEnabledAsDefault
      ? enabledButtons.find(b => b.default) || enabledButtons[0] || buttons[0]
      : buttons.find(b => b.default) || buttons[0];
  }, [
    buttons,
    defaultButtonTitleFromSettings,
    enabledButtons,
    preferEnabledAsDefault,
  ]);

  const showMore = buttons.some(b => b !== defaultButton);

  if (!defaultButton) {
    return null;
  }

  const defaultHasSubMenu = defaultButton?.subNavItems?.length > 0;

  // Wired up as onClickCapture below - ag-Grid's row-click-to-select
  // listener is a native bubble-phase listener on an ancestor row
  // container, so a regular onClick's stopPropagation() always runs too
  // late to stop it. The capture phase runs first, so it can.
  const defaultOnClick = e => {
    e.stopPropagation();
    if (defaultButton) {
      if (defaultButton.onClick) {
        defaultButton.onClick(e);
      }

      if (defaultHasSubMenu) {
        openDefaultButtonMenu();
      }
    }
  };

  const moreButtons = buttons.filter(button => button !== defaultButton);
  const ButtonGroupComponent = flex ? FlexButtonGroup : ButtonGroup;
  return (
    <>
      <ButtonGroupComponent
        fullWidth={fullWidth}
        variant="contained"
        ref={anchorRef}
        disableElevation
        type="button"
        orientation={orientation}
        data-tour={buttonGroupDataTour}
      >
        <ButtonComponent
          onClickCapture={defaultOnClick}
          disabled={defaultButton.isDisabled}
          data-tour={defaultButton.dataTour || ""}
          flex={flex ? 1 : undefined}
          inline={buttonInline}
          title={defaultButton.tooltip || defaultButton.title}
          type="button"
        >
          <IconButtonContents
            startIcon={defaultButton.icon}
            spin={defaultButton.spin}
            title={defaultButton.title}
          />
        </ButtonComponent>
        {showMore ? (
          <ContextMenuPopoverButton
            ButtonComponent={ButtonComponent}
            anchorRef={anchorRef}
            buttons={moreButtons}
            stickySetting={stickySetting}
            includeSearchSubNavThreshold={includeSearchSubNavThreshold}
            restrictHeightSubNav={restrictHeightSubNav}
            optionsButtonDataTour={optionsButtonDataTour}
            popoverHorizontal={popoverHorizontal}
            label=""
          />
        ) : null}
      </ButtonGroupComponent>
      {defaultHasSubMenu ? (
        <SubMenuPopover
          subNavItems={defaultButton.subNavItems}
          anchorEl={anchorRef.current}
          onClose={closeDefaultButtonMenu}
          open={isDefaultButtonMenuOpen}
          includeSearchSubNavThreshold={includeSearchSubNavThreshold}
          restrictHeightSubNav={restrictHeightSubNav}
        />
      ) : null}
    </>
  );
};

export default MultiButtonComponent;
